import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

const DarijaTranslator = () => {
  const [searchParams] = useSearchParams();
  const language = searchParams.get('language') || 'tn';

  const [sourceText, setSourceText] = useState('');

  // Langue source par défaut selon le paramètre "language"
  const initialSrc =
    language === 'tn' ? 'aeb_Arab' :
    language === 'ma' ? 'ary_Arab' :
    language === 'amazigh' ? 'tzm_Tfng' :
    language === 'bambara' ? 'bam_Latn' :
    language === 'lingala' ? 'lin_Latn' :
    language === 'eg' ? 'arz_Arab' : // correctif (égyptien)
    'eng_Latn';

  const [sourceLang, setSourceLang] = useState(initialSrc);     // valeur affichée (UI)
  const [sourceLangCode, setSourceLangCode] = useState(initialSrc); // code réel envoyé à l'API

  const [targetLang, setTargetLang] = useState('eng_Latn');
  const [translatedText, setTranslatedText] = useState('');
  const [isTranslating, setIsTranslating] = useState(false);
  const [selectedExample, setSelectedExample] = useState('');
  const initialized = useRef(false);
  const swapInProgress = useRef(false);
  const manualLangChange = useRef(false);

  // --- Config langues ---
  const languageConfig = {
    tn: {
      title: 'Tunisian Arabic Translator',
      description: 'Translate from Tunisian Arabic to English with our advanced AI model.',
      sourceOptions: [{ value: 'aeb_Arab', label: 'Tunisian Arabic', code: 'aeb_Arab' }],
      targetOptions: [{ value: 'eng_Latn', label: 'English', code: 'eng_Latn' }],
      examples: {
        aeb_Arab: [
          "شنوة الفرق بين ال 5G و 4G؟",
          "Chnoua najem na3mel باش نلغي l'abonnement?",
          "حاب نبدل الفورفي متاعي بفورفي فيه أكثر داتا.",
          "5edmat mt3 Ooredoo slow barcha fel soirée",
          "الانترنت ديما تقطع في الليل، شنوة السبب؟",
          "Nheb na3ref chnoua el démarches باش نحل ligne jdida.",
          "حبيت نسأل إذا فما عرض خاص للموديم ولا لا؟",
          "Yejini SMS kol chhar l'offre yetjadded automatique و أنا ما طلبتش هذا.",
          "نحب نعرف كيفاش نشتري رصيد انترنات للهاتف متاعي"
        ]
      }
    },

    ma: {
      title: 'Moroccan Arabic Translator',
      description: 'Translate between Moroccan Arabic, French, English and Spanish with our advanced AI model.',
      sourceOptions: [
        { value: 'ary_Arab', label: 'Moroccan Arabic', code: 'ary_Arab' },
        { value: 'eng_Latn', label: 'English', code: 'eng_Latn' },
        { value: 'fra_Latn', label: 'French', code: 'fra_Latn' },
        { value: 'spa_Latn', label: 'Spanish', code: 'spa_Latn' },
      ],
      targetOptions: (src) => {
        const base = [
          { value: 'eng_Latn', label: 'English', code: 'eng_Latn' },
          { value: 'fra_Latn', label: 'French', code: 'fra_Latn' },
          { value: 'spa_Latn', label: 'Spanish', code: 'spa_Latn' },
        ];
        if (src !== 'ary_Arab') {
          return [
            ...base,
            { value: 'ary_Arab', label: 'Moroccan Arabic', code: 'ary_Arab' },
            { value: 'ary_Latn', label: 'Moroccan Arabic (Latin letters)', code: 'ary_Latn' },
          ].filter(o => o.value !== src);
        }
        return base;
      },
      examples: {
        ary_Arab: [
          "شحال ديال الفلوس كاتشد فالصالير ديالك؟",
          "Bghit ndir virement international men compte l compte, wach kathtajou IBAN w SWIFT o chhal hiya l commission?",
          "Bghit nbadal l forfait diali o nzid internet, wach kayna couverture mzyana dial fibre f had la région?",
          "شنو الفرق بين 5G و 4G؟",
          "كيفاش نقدر نلغي الاشتراك؟",
          "بغيت نعرف شنو الإجراءات باش نفتح خط جديد.",
          "بغيت نسول واش كاين عرض خاص ل lmodem",
          "كيجيني SMS كل شهر العرض كيتجدد أوتوماتيك وأنا ما طلبتش هادشي.",
          "بغيت نعرف كيفاش نشري رصيد انترنت للتيليفون ديالي"
        ],
        eng_Latn: [
          "What's the difference between 5G and 4G?",
          "How can I cancel my subscription?",
          "I want to change my plan to one with more data.",
          "Internet always cuts out at night, why?",
          "I want to know what the steps are to open a new line.",
          "I'd like to know if there's a special offer for modems.",
          "I get an SMS every month that the offer renews automatically but I didn't ask for this.",
          "I want to know how to buy internet credit for my phone"
        ],
        fra_Latn: [
          "Quelle est la différence entre la 5G et la 4G ?",
          "Comment puis-je annuler mon abonnement ?",
          "Je veux changer mon forfait pour un forfait avec plus de données.",
          "Internet coupe toujours la nuit, pourquoi ?",
          "Je veux savoir quelles sont les démarches pour ouvrir une nouvelle ligne.",
          "Je voudrais savoir s'il y a une offre spéciale pour les modems.",
          "Je reçois un SMS chaque mois que l'offre se renouvelle automatiquement mais je n'ai pas demandé cela.",
          "Je veux savoir comment acheter du crédit internet pour mon téléphone"
        ],
        spa_Latn: [
          "¿Cuál es la différence entre 5G y 4G?",
          "¿Cómo puedo cancelar mi suscripción?",
          "Quiero cambiar mi plan a uno con más datos.",
          "Internet siempre se corta por la noche, ¿por qué?",
          "Quiero saber cuáles son los pasos para abrir una nueva línea.",
          "Me gustaría saber si hay una oferta especial para módems.",
          "Recibo un SMS cada mes diciendo que la oferta se renueva automáticamente pero no lo pedí.",
          "Quiero saber cómo comprar crédito de internet para mi teléfono"
        ],
      }
    },

    eg: {
      title: 'Egyptian Arabic Translator',
      description: 'Translate between Egyptian Arabic (Amiya) and English with our advanced AI model.',
      sourceOptions: [
        { value: 'arz_Arab', label: 'Egyptian Arabic', code: 'arz_Arab' },
        { value: 'eng_Latn', label: 'English', code: 'eng_Latn' }
      ],
      targetOptions: (src) =>
        src === 'arz_Arab'
          ? [{ value: 'eng_Latn', label: 'English', code: 'eng_Latn' }]
          : [{ value: 'arz_Arab', label: 'Egyptian Arabic', code: 'arz_Arab' }],
      examples: {
        arz_Arab: [
          "ايه الفرق بين الـ 5G والـ 4G؟",
          "النت بيشتغل ببطء بعد الساعة ٨",
          "مش لاقي شبكة في الشغل خالص",
          "إزاي أشحن الباقة من التطبيق؟",
          "فين أقرب فرع لخدمة العملاء؟",
          "ليه الميجابايتس بتخلص بسرعة؟",
          "عايز أعرف لو فيه عرض خاص للمودم.",
          "عايز أنقل رقمي لشركة تانية",
          "الاتصال ضعيف جدًا في منطقتي"
        ],
        eng_Latn: [
          "The 4G coverage is weak in my area.",
          "How can I cancel my subscription?",
          "I want to change my plan to one with more data.",
          "The network gets really slow in the evening.",
          "Internet always cuts out at night, why?",
          "I want to know what the steps are to open a new line.",
          "I'd like to know if there's a special offer for modems.",
          "I get an SMS every month that the offer renews automatically but I didn't ask for this.",
          "The call quality is very bad."
        ]
      }
    },

    amazigh: {
      title: 'Amazigh Translator',
      description: 'Translate between Amazigh, French, English and Arabic with our advanced AI model.',
      sourceOptions: [
        { value: 'tzm_Tfng', label: 'Amazigh', code: 'tzm_Tfng' },
        { value: 'eng_Latn', label: 'English', code: 'eng_Latn' },
        { value: 'fra_Latn', label: 'French', code: 'fra_Latn' },
        { value: 'arb_Arab', label: 'Arabic', code: 'arb_Arab' }
      ],
      targetOptions: (sourceLang) => {
        const options = [
          { value: 'eng_Latn', label: 'English', code: 'eng_Latn' },
          { value: 'fra_Latn', label: 'French', code: 'fra_Latn' }
        ];
        if (sourceLang !== 'arb_Arab') {
          options.push({ value: 'arb_Arab', label: 'Arabic', code: 'arb_Arab' });
        }
        if (sourceLang !== 'tzm_Tfng') {
          options.push({ value: 'tzm_Tfng', label: 'Amazigh', code: 'tzm_Tfng' });
        }
        return options.filter(opt => opt.value !== sourceLang);
      },
      examples: {
        tzm_Tfng: [
          "ⴰⴼⴼⴰⵔ ⴰⵏⴻⵔⵎⵉⵙ ⴷ ⵉⵎⵓⴹⴰⵏ.",
          "ⵔⵉⵖ ⴰⴷ ⵣⵕⴻⵖ ⴰⵎⵚⵚⵉⵡⴹ ⵏ ⵡⵓⵍ.",
          "ⴰⵎⴳⴰⵍ ⵏ ⵓⵙⴰⴹⵓⴼ ⵉⵜⵜⴰⵡⵉ ⵙ ⵜⵉⵙⵖⴰⵍ.",
          "ⵜⴰⵎⴰⵡⴰⵙⵜ ⵏ ⴱⵕⵕⴰ.",
          "ⵉⴼⴽⴰ ⴱⴰⴱ ⵏ ⵡⴰⴷⴷⵓⵔ ⴷⵉ ⵕⵕⴱⴰⵟ ⵜⵉⵡⵓⵔⵉⵡⵉⵏ ⵍⴱⴷⵓ ⵏ ⵓⵙⴽⵔ ⵏ ⵓⵣⵔⵉⴳ ⵏ ⵡⵓⵣⵣⴰⵍ ⵉⵖⴰⵡⵍⵏ ⴱⴰⵀⵔⴰ ⵉⵣⴷⵉⵏ ⵍⵇⵏⵉⵟⵕⴰ ⴷ ⵎⵕⵕⴰⴽⵯⵛ.",
          "ⴱⴰⵔⵉⵣ - ⵉⵙⵎⵖⵔ ⵓⵏⵙⵙⵉⵅⴼ ⵏ ⵜⴳⴷⵓⴷⴰⵏⵜ ⵏ ⴼⵕⴰⵏⵚⴰ ⵎⴰⵙⵙ ⵉⵎⴰⵏⵡⵉⵍ ⵎⴰⴽⵔⵓⵏ ⵜⵉⴽⴽⵉ ⵏ ⴱⴰⴱ ⵏ ⵡⴰⴷⴷⵓⵔ ⴰⴳⵍⵍⵉⴷ ⵎⵓⵃⵎⵎⴷ ⵡⵉⵙⵙ ⵚⴹⵉⵚ",
          "ⵉⴹⵍⵍⵉ ⵡⴰⵙⵙ ⵏ ⵓⴽⵡⴰⵙ ⵍⴱⴷⵓ ⵓⵙⴽⵔ ⵏ ⵓⵣⵔⵉⴳ ⵏ ⵡⵓⵣⵣⴰⵍ ⵉⵖⴰⵡⵍⵏ ⴱⴰⵀⵔⴰ ⵉⵣⴷⵉⵏ ⵍⵇⵏⵉⵟⵕⴰ ⴷ ⵎⵕⵕⴰⴽⵯⵛ.",
          "ⵜⵉⴱⵔⴰⵜⴰⵜⵜⵉⵏ ⵏ ⵓⵙⵔⵖⵓⴷ ⵏ ⴱⴰⴱ ⵏ ⵡⴰⴷⴷⵓⵔ ⴰⴳⵍⵍⵉⴷ ⵉ ⵢⵏⵙⵙⵉⵅⴼⵏ ⵏ ⵉⵡⴰⵏⴽⵏ ⵏ ⵜⵎⵉⵣⴰⵔ ⵜⵉⵎⵓⵏⵙⵍⵎⵏ ⵙ ⵓⵣⵎⵣⵣⵓ ⵏ ⵄⵉⴷ ⵍⴼⵉⵟⵕ",
          "ⵉⵎⵓⵙⵙⵓⵜⵏ ⵏ ⵉⴳⵍⴷⵓⵏⵏ."
        ],
        eng_Latn: [
          "What's the difference between 5G and 4G?",
          "I want to change my plan to one with more data.",
          "Ooredoo's service is very slow in the evening",
          "Internet always cuts out at night, why?",
          "I want to know what the steps are to open a new line.",
          "I want to know how to buy internet credit for my phone.",
          "Ministry of Interior.",
          "left the group",
          "Pinned a location"
        ],
        fra_Latn: [
          "Quelle est la différence entre la 5G et la 4G ?",
          "Comment puis-je annuler mon abonnement ?",
          "Je veux changer mon forfait pour un forfait avec plus de données.",
          "Le service de Ooredoo est très lent le soir",
          "Internet coupe toujours la nuit, pourquoi ?",
          "Je veux savoir quelles sont les démarches pour ouvrir une nouvelle ligne.",
          "Je voudrais savoir s'il y a une offre spéciale pour les modems.",
          "Je reçois un SMS chaque mois que l'offre se renouvelle automatiquement mais je n'ai pas demandé cela.",
          "Je veux savoir comment acheter du crédit internet pour mon téléphone."
        ],
        arb_Arab: [
          "ما الفرق بين 5G و 4G؟",
          "أين يمكنني شراء هاتف جديد؟",
          "أريد استرجاع ملفي الدي يحتوي على الكثير من البيانات.",
          "خدمة أوريدو بطيئة جدًا في المساء",
          "ينقطع الإنترنت دائمًا في الليل، لماذا؟",
          "أريد أن أعرف ما هي الخطوات لإنشاء حساب جديد.",
          "أتلقى رسالة نصية كل شهر تقول إن العرض متجدد بانتظام، لكنني لم أطلب ذلك.",
          "أود أن أعرف ما إذا كان هناك عرض خاص لأجهزة المودم.",
          "أريد أن أعرف كيفية شراء بطاقة إنترنت جديدة."
        ]
      }
    },

    bambara: {
      title: 'Bambara Translator',
      description: 'Translate between Bambara, French and English with our advanced AI model.',
      sourceOptions: [
        { value: 'bam_Latn', label: 'Bambara', code: 'bam_Latn' },
        { value: 'eng_Latn', label: 'English', code: 'eng_Latn' },
        { value: 'fra_Latn', label: 'French', code: 'fra_Latn' }
      ],
      targetOptions: (sourceLang) => {
        const options = [
          { value: 'eng_Latn', label: 'English', code: 'eng_Latn' },
          { value: 'fra_Latn', label: 'French', code: 'fra_Latn' }
        ];
        if (sourceLang !== 'bam_Latn') {
          options.push({ value: 'bam_Latn', label: 'Bambara', code: 'bam_Latn' });
        }
        return options.filter(opt => opt.value !== sourceLang);
      },
      examples: {
        bam_Latn: [
          "5G ani 4G bɛ mun na fara ɲɔgɔn na?",
          "N bɛ se ka taa internet la wa?",
          "Ne bɛ fɔ ka ne ka forufi labɛn forufi kelen na min bɛ ka data caman sɔrɔ.",
          "Internet bɛ taa ni kɛrɛkɛrɛ.",
          "Ne bɛ se ka taa sɔgɔma.",
          "Sini tile bɛ bɔ na, n ye sɛbɛn kɛ.",
          "Yala modemw ye jatebɔ suguya sɔrɔ wa?",
          "N b'a fɛ ka wari sɔrɔ banki la",
          "Hadamaden josiraw dantigɛkan"
        ],
        eng_Latn: [
          "What's the difference between 5G and 4G?",
          "How can I cancel my subscription?",
          "I want to change my plan to one with more data.",
          "Internet connection is very slow during the evening hours.",
          "Internet always cuts out at night, why?",
          "I want to know what the steps are to open a new line.",
          "Is there a special offer for modems?",
          "I get an SMS every month that the offer renews automatically but I didn't ask for this.",
          "I want to know how to buy internet credit for my phone."
        ],
        fra_Latn: [
          "Quelle est la différence entre la 5G et la 4G ?",
          "Comment puis-je annuler mon abonnement ?",
          "Je veux changer mon forfait pour un forfait avec plus de données.",
          "La connexion Internet est très lente en soirée.",
          "Internet coupe toujours la nuit, pourquoi ?",
          "Je veux savoir quelles sont les démarches pour ouvrir une nouvelle ligne.",
          "Y a-t-il une offre spéciale pour les modems ?",
          "Je reçois un SMS chaque mois que l'offre se renouvelle automatiquement mais je n'ai pas demandé cela.",
          "Je veux savoir comment acheter du crédit internet pour mon téléphone."
        ]
      }
    },

    lingala: {
      title: 'Lingala Translator',
      description: 'Translate between Lingala, French and English with our advanced AI model.',
      sourceOptions: [
        { value: 'lin_Latn', label: 'Lingala', code: 'lin_Latn' },
        { value: 'eng_Latn', label: 'English', code: 'eng_Latn' },
        { value: 'fra_Latn', label: 'French', code: 'fra_Latn' }
      ],
      targetOptions: (sourceLang) => {
        const options = [
          { value: 'eng_Latn', label: 'English', code: 'eng_Latn' },
          { value: 'fra_Latn', label: 'French', code: 'fra_Latn' }
        ];
        if (sourceLang !== 'lin_Latn') {
          options.push({ value: 'lin_Latn', label: 'Lingala', code: 'lin_Latn' });
        }
        return options.filter(opt => opt.value !== sourceLang);
      },
      examples: {
        lin_Latn: [
          "Esika ezali na bokeseni kati na 5G mpe 4G?",
          "Nalingi kobongisa forfait na ngai na forfait mosusu oyo ezali na data ebele.",
          "Service ya Ooredoo ezali kokende malamu mingi na mpokwa.",
          "Internet ezali kokweya ntango nyonso na butu, mpo na nini?",
          "Nalingi koyeba plan ya déploiement ya 5G na Kinshasa mpe na provinces.",
          "Nalingi natinda crédits epai ya moninga, nakoki kosala yango na ndenge nini ?",
          "Bokoki kotindela ngai relevé bancaire officiel na email pona usage administratif ?",
          "Soki modem esengeli kosepeli na mise à jour, bokoki kosala yango à distance ?",
          "Bozali na possibilité ya kofungola package spécial pona étudiants ?"
        ],
        eng_Latn: [
          "What's the difference between 5G and 4G?",
          "Is there a plan available that includes more internet data?",
          "I often lose signal when I'm at home.",
          "Internet always cuts out at night, why?",
          "When will 5G be deployed in Kinshasa and the provinces?",
          "Can we transfer credits to each other?",
          "Can you send me official bank statements by email for administrative use?",
          "The internet keeps disconnecting every few minutes.",
          "Is there a customer support number I can call if I have a problem?"
        ],
        fra_Latn: [
          "Quelle est la différence entre la 5G et la 4G ?",
          "Existe-t-il un forfait qui inclut plus de données internet ?",
          "Je perds souvent le signal quand je suis à la maison.",
          "Internet se coupe toujours la nuit, pourquoi ?",
          "Quand la 5G sera-t-elle déployée à Kinshasa et dans les provinces ?",
          "Pouvons-nous nous transférer des crédits entre nous ?",
          "Pouvez-vous m'envoyer des relevés bancaires officiels par email pour un usage administratif ?",
          "La connexion internet se déconnecte toutes les quelques minutes.",
          "Y a-t-il un numéro du service client que je peux appeler en cas de problème ?"
        ]
      }
    }
  };

  // ✅ currentConfig défini ici (corrige ReferenceError)
  const currentConfig = languageConfig[language] || {
    title: 'Translator',
    description: '',
    sourceOptions: [],
    targetOptions: [],
    examples: {}
  };

  // ✅ État pour les options de cible + initialisation
  const initialTargetOptions = typeof currentConfig.targetOptions === 'function'
    ? currentConfig.targetOptions(initialSrc)
    : (currentConfig.targetOptions || []);
  const [targetOptions, setTargetOptions] = useState(initialTargetOptions);

  // Exemple aléatoire
  const loadRandomExample = () => {
    const list = currentConfig.examples?.[sourceLang];
    if (Array.isArray(list) && list.length) {
      const ex = list[Math.floor(Math.random() * list.length)];
      setSourceText(ex);
      setSelectedExample(ex);
    }
  };

  // Traduction (avec override du code source si nécessaire)
  const translateText = async (text, tgtLang, overrideSrcCode = null) => {
    setIsTranslating(true);
    try {
      if (!text.trim()) {
        setTranslatedText('');
        return;
      }
      const srcCodeToSend = overrideSrcCode ?? sourceLangCode;
      const res = await fetch(`${process.env.REACT_APP_API_URL}/translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text,
          src_lang: srcCodeToSend,
          tgt_lang: tgtLang,
          language
        }),
      });
      if (!res.ok) throw new Error('API error');
      const data = await res.json();
      const clean = String(data.translation || '')
        .replace(/^(ary_Latn|ary|eng|fra|spa|tzm|arb|bam|lin|arz)\s+/, '');
      setTranslatedText(clean);
    } catch (e) {
      setTranslatedText('❌ Error in translation');
    } finally {
      setIsTranslating(false);
    }
  };

  // Auto-traduction (debounce)
  useEffect(() => {
    if (!sourceText.trim()) {
      setTranslatedText('');
      return;
    }
    if (!swapInProgress.current) {
      const t = setTimeout(() => translateText(sourceText, targetLang), 500);
      return () => clearTimeout(t);
    }
    swapInProgress.current = false;
  }, [sourceText, targetLang, sourceLangCode]);

  // Mettre à jour les cibles quand la source (UI) change
  useEffect(() => {
    if (typeof currentConfig.targetOptions === 'function') {
      const newOpts = currentConfig.targetOptions(sourceLang);
      setTargetOptions(newOpts);
      if (!newOpts.some(o => o.value === targetLang)) {
        setTargetLang(newOpts[0]?.value || '');
      }
    } else {
      const arr = currentConfig.targetOptions || [];
      setTargetOptions(arr);
      if (!arr.some(o => o.value === targetLang)) {
        setTargetLang(arr[0]?.value || '');
      }
    }
    setTranslatedText('');
    if (manualLangChange.current) {
      loadRandomExample();
      manualLangChange.current = false;
    }
  }, [sourceLang, language]); // currentConfig varie avec "language"

  // Init : charger un exemple au premier rendu
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    loadRandomExample();
  }, []);

  // Désélectionner l'exemple si texte édité
  useEffect(() => {
    if (sourceText !== selectedExample && selectedExample !== '') {
      setSelectedExample('');
    }
  }, [sourceText, selectedExample]);

  const isRTL = (text) => {
    const arabicChars = text.match(/[\u0600-\u06FF]/g) || [];
    return arabicChars.length / Math.max(text.length || 1, 1) > 0.3;
  };

  const handleSourceLanguageChange = (e) => {
    manualLangChange.current = true;
    const val = e.target.value;
    setSourceLang(val);        // UI
    setSourceLangCode(val);    // Code réel par défaut
    setTranslatedText('');
  };

  const handleTargetLanguageChange = (e) => {
    setTargetLang(e.target.value);
    setTranslatedText('');
  };

  // Swap : cas marocain → UI = ary_Arab, mais code réel peut être ary_Arab / ary_Latn
  const handleSwapLanguages = () => {
    if (!translatedText || sourceLang === targetLang) return;

    swapInProgress.current = true;
    const newSourceText = translatedText;

    // valeurs "théoriques" après swap
    let newSourceDisplay = targetLang; // UI
    let newSourceCode = targetLang;    // API

    if (language === 'ma' && (targetLang === 'ary_Arab' || targetLang === 'ary_Latn')) {
      newSourceDisplay = 'ary_Arab';   // UI = toujours "Moroccan Arabic"
      newSourceCode = targetLang;      // code réel conservé
    }

    const newTarget = sourceLangCode;  // ancienne source (code réel) devient la cible

    setSourceLang(newSourceDisplay);
    setSourceLangCode(newSourceCode);
    setTargetLang(newTarget);

    setSourceText(newSourceText);
    setTranslatedText('');

    if (newSourceText.trim()) {
      translateText(newSourceText, newTarget, newSourceCode); // override immédiat
    }
  };

  const handleExampleClick = (example) => {
    setSourceText(example);
    setSelectedExample(example);
    setTranslatedText('');
  };

  return (
    <div style={{
      maxWidth: '1000px',
      margin: '0 auto',
      padding: '20px',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      backgroundColor: '#f8f9fa'
    }}>
      {/* Header */}
      <div style={{
        textAlign: 'center',
        marginBottom: '30px',
        padding: '20px',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f2937', marginBottom: '8px' }}>
          {currentConfig.title}
        </h1>
        <p style={{ fontSize: '16px', color: '#6b7280', marginBottom: '16px' }}>
          {currentConfig.description}
        </p>
      </div>

      {/* Translator Container */}
      <div style={{
        display: 'flex',
        gap: '20px',
        alignItems: 'flex-start',
        marginBottom: '30px',
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        {/* Source */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <select
            value={sourceLang}
            onChange={handleSourceLanguageChange}
            style={{
              width: '100%',
              height: '50px',
              fontSize: '16px',
              padding: '0 10px',
              borderRadius: '6px',
              border: '1px solid #ddd',
              marginBottom: '10px',
              cursor: 'pointer',
              backgroundColor: '#f8f9fa'
            }}
          >
            {(currentConfig.sourceOptions || []).map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>

          <textarea
            placeholder="Enter text"
            value={sourceText}
            onChange={(e) => setSourceText(e.target.value)}
            style={{
              width: '100%',
              height: '260px',
              fontSize: '16px',
              padding: '10px',
              direction: isRTL(sourceText) ? 'rtl' : 'ltr',
              borderRadius: '6px',
              border: '1px solid #ddd',
              resize: 'none',
              boxSizing: 'border-box',
              backgroundColor: '#f8f9fa'
            }}
          />
        </div>

        {/* Swap */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '60px' }}>
          <button
            onClick={handleSwapLanguages}
            disabled={language === 'tn'}
            style={{
              backgroundColor: language === 'tn' ? '#e9ecef' : '#f1f3f5',
              border: 'none',
              borderRadius: '50%',
              width: '50px',
              height: '50px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: language === 'tn' ? 'not-allowed' : 'pointer',
              fontSize: '22px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              opacity: language === 'tn' ? 0.5 : 1
            }}
          >
            ⇄
          </button>
        </div>

        {/* Target */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <select
            value={targetLang}
            onChange={handleTargetLanguageChange}
            style={{
              width: '100%',
              height: '50px',
              fontSize: '16px',
              padding: '0 10px',
              borderRadius: '6px',
              border: '1px solid #ddd',
              marginBottom: '10px',
              cursor: 'pointer',
              backgroundColor: '#f8f9fa'
            }}
          >
            {targetOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>

          <div style={{ position: 'relative' }}>
            <textarea
              readOnly
              value={translatedText}
              placeholder="Translation will appear here..."
              style={{
                width: '100%',
                height: '260px',
                fontSize: '16px',
                padding: '10px',
                direction: isRTL(translatedText) ? 'rtl' : 'ltr',
                backgroundColor: '#f8f9fa',
                border: '1px solid #ddd',
                borderRadius: '6px',
                boxSizing: 'border-box',
                resize: 'none'
              }}
            />
            {isTranslating && (
              <div style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                backgroundColor: 'rgba(255,255,255,0.9)',
                padding: '2px 8px',
                borderRadius: '4px',
                fontSize: '14px',
                color: '#6b7280',
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
              }}>
                Translating...
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Examples */}
      <div style={{
        marginBottom: '20px',
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{
          fontSize: '16px',
          fontWeight: 600,
          color: '#1f2937',
          marginBottom: '16px',
          paddingBottom: '8px',
          borderBottom: '1px solid #e5e7eb'
        }}>
          Example Phrases
        </h3>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: '12px'
        }}>
          {(currentConfig.examples?.[sourceLang] || []).map((ex, i) => (
            <button
              key={i}
              onClick={() => handleExampleClick(ex)}
              style={{
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                backgroundColor: ex === selectedExample ? '#fee2e2' : '#f8f9fa',
                textAlign: 'left',
                cursor: 'pointer',
                transition: 'all 0.2s',
                direction: isRTL(ex) ? 'rtl' : 'ltr',
                fontSize: '14px',
                color: '#1f2937',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
              }}
              onMouseOver={(e) => {
                if (ex !== selectedExample) {
                  e.currentTarget.style.backgroundColor = '#f1f3f5';
                  e.currentTarget.style.borderColor = '#d1d5db';
                }
              }}
              onMouseOut={(e) => {
                if (ex !== selectedExample) {
                  e.currentTarget.style.backgroundColor = '#f8f9fa';
                  e.currentTarget.style.borderColor = '#e5e7eb';
                }
              }}
            >
              {ex}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DarijaTranslator;
